import {Box, Link, Typography} from "@mui/material"
import {FC, Fragment, useState} from "react"
import {ContactModal} from "../.."
import {useConfigProvider} from "../../../config"
import {decodeToken} from "react-jwt"
import {AccountTeam, Token} from "../../../types"
import useStorage, {StorageData} from "../../../hooks/useStorage"
import {accountEndpoints} from "../../../services"
import {useQuery} from "@tanstack/react-query"
import {CrmAccount} from "../../../types/CrmAccount"
import {useTranslation} from "react-i18next"

export const ErrorMessageAskExpert: FC = () => {
  const storage = useStorage("session", StorageData.crmAccount)
  const {token} = useConfigProvider()
  const decoded = decodeToken<Token>(token)
  const {t} = useTranslation()
  const accountNumber =
    decoded?.companyId === CrmAccount.Internal
      ? storage.get() || ""
      : decoded?.companyId || ""
  const [showContactTeam, setShowContactTeam] = useState(false)

  const {getAccountTeamByAccountNumber, getAccountByAccountNumber} =
    accountEndpoints()

  const getAccounTeamQuery = useQuery({
    queryKey: ["accountTeam", accountNumber],
    queryFn: () => getAccountTeamByAccountNumber(accountNumber),
    enabled: !!accountNumber && accountNumber !== CrmAccount.Internal,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const getAccountQuery = useQuery({
    queryKey: ["account", accountNumber],
    queryFn: () => getAccountByAccountNumber(accountNumber),
    enabled: !!accountNumber && accountNumber !== CrmAccount.Internal,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const accountManager = {
    accountTeamEmailAddress: getAccountQuery.data?.relationshipManagerEmail!,
    accountTeamFullName: getAccountQuery.data?.relationshipManager,
    accountTeamId: "",
    crmAccountId: getAccountQuery.data?.crmAccountId,
    crmAccountName: getAccountQuery.data?.crmAccountName,
    crmAccountNumber: accountNumber,
  } as AccountTeam

  const handleAskExpertClick = async () => {
    const ccAddresses = []

    if (accountManager.accountTeamEmailAddress) {
      ccAddresses.push(accountManager.accountTeamEmailAddress)
    }

    getAccounTeamQuery.data?.forEach((member) => {
      if (member.accountTeamEmailAddress) {
        ccAddresses.push(member.accountTeamEmailAddress)
      }
    })
    const event = new CustomEvent("ask-an-expert", {
      detail: {
        show: true,
        relatesTo: "General",
        ccAddresses: ccAddresses,
      },
    })
    window.dispatchEvent(event)
  }

  const handleAccountManagerClick = async () => {
    setShowContactTeam((s) => !s)
  }

  return (
    <Fragment>
      <Box
        sx={{
          flexGrow: 0,
          fontFamily: "Open Sans",
          fontSize: "14px",
          fontWeight: "normal",
          fontStretch: "normal",
          fontStyle: "normal",
          lineHeight: 1.43,
          letterSpacing: "normal",
          textAlign: "center",
          color: "#595958",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
          }}
        >
          {t(`We're having trouble with this connection right now.`)}
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
          }}
        >
          {t("Please")}{" "}
          <Link
            sx={{
              lineHeight: 1.14,
              color: "#226ca6",
              cursor: "pointer",
            }}
            onClick={handleAskExpertClick}
          >
            {t("Ask an Expert")}
          </Link>{" "}
          {t("or contact your")}{" "}
          <Link
            sx={{
              lineHeight: 1.14,
              color: "#226ca6",
              cursor: "pointer",
            }}
            onClick={handleAccountManagerClick}
          >
            {t("Account Manager")}
          </Link>
          .
        </Typography>
      </Box>

      <ContactModal
        show={showContactTeam}
        handleToggle={() => setShowContactTeam((s) => !s)}
        member={accountManager!!}
        account={getAccountQuery.data!!}
        accountTeam={getAccounTeamQuery.data!!}
      />
    </Fragment>
  )
}

export const ExpertNoResultMessage: FC = () => {
  const storage = useStorage("session", StorageData.crmAccount)
  const {token} = useConfigProvider()
  const decoded = decodeToken<Token>(token)
  const accountNumber =
    decoded?.companyId === CrmAccount.Internal
      ? storage.get() || ""
      : decoded?.companyId || ""
  const [showContactTeam, setShowContactTeam] = useState(false)
  const {getAccountTeamByAccountNumber, getAccountByAccountNumber} =
    accountEndpoints()

  const getAccounTeamQuery = useQuery({
    queryKey: ["accountTeam", accountNumber],
    queryFn: () => getAccountTeamByAccountNumber(accountNumber),
    enabled: !!accountNumber && accountNumber !== CrmAccount.Internal,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const getAccountQuery = useQuery({
    queryKey: ["account", accountNumber],
    queryFn: () => getAccountByAccountNumber(accountNumber),
    enabled: !!accountNumber && accountNumber !== CrmAccount.Internal,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const accountManager = {
    accountTeamEmailAddress: getAccountQuery.data?.relationshipManagerEmail!,
    accountTeamFullName: getAccountQuery.data?.relationshipManager,
    accountTeamId: "",
    crmAccountId: getAccountQuery.data?.crmAccountId,
    crmAccountName: getAccountQuery.data?.crmAccountName,
    crmAccountNumber: accountNumber,
  } as AccountTeam

  const handleAccountManagerClick = async () => {
    setShowContactTeam((s) => !s)
  }

  return (
    <Box>
      <Typography
        style={{
          color: "#595958",
          fontSize: "14px",
          lineHeight: "20px",
          textAlign: "center",
          fontFamily: "Open Sans",
        }}
      >
        No results - try reducing your search criteria or contact your&nbsp;
        <Link
          style={{
            color: "#226ca6",
            fontSize: "14px",
            lineHeight: "16px",
            fontFamily: "Open Sans",
            cursor: "pointer",
          }}
          onClick={handleAccountManagerClick}
        >
          Account Manager
        </Link>
        &nbsp;for more information.
      </Typography>
      <ContactModal
        show={showContactTeam}
        handleToggle={() => setShowContactTeam((s) => !s)}
        member={accountManager!!}
        account={getAccountQuery.data!!}
        accountTeam={getAccounTeamQuery.data!!}
      />
    </Box>
  )
}
